/**
 * 📸 EquipmentScannerPanel — snap → AI-identify → confirm → save,
 * plus loadout list + "Cook / Workout with what you have" AI session.
 *
 * Lives inside the ARBottomSheet drawer on the Interactive Gym & Kitchen
 * pages. Requires a live camera stream from the parent shell (video ref
 * is captured via the `onCameraReady` hook or a document.querySelector
 * fallback).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Check, X, Trash2, Sparkles, Loader2, ChefHat, Dumbbell, RefreshCcw, GraduationCap, Image as ImageIcon } from "lucide-react";
import { GuidedSessionPlayer } from "./GuidedSessionPlayer";
import { CameraCapture } from "./CameraCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EquipmentScannerPanel({ space }) {
  const spaceLabel = space === "kitchen" ? "kitchen" : "gym";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [pending, setPending] = useState(null); // { photo_b64, name, category, tags, ai_confidence, source }
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderGoal, setBuilderGoal] = useState("");
  const [difficulty, setDifficulty] = useState("explain_why");
  const [builderResult, setBuilderResult] = useState(null);
  const [builderBusy, setBuilderBusy] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  // 🖼️ Room-builder gallery — snap photos to progressively build the space
  const [roomShots, setRoomShots] = useState([]);
  const [roomBusy, setRoomBusy] = useState(false);
  const nameEditRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/equipment/list`, { params: { space } });
      setItems(r.data?.items || []);
      setCats(r.data?.categories?.[space] || []);
    } catch {
      /* silent */
    }
  }, [space]);

  const refreshRoom = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/room/gallery`, { params: { space, limit: 60 } });
      setRoomShots(r.data?.snapshots || []);
    } catch {
      /* silent */
    }
  }, [space]);

  useEffect(() => { refresh(); refreshRoom(); }, [refresh, refreshRoom]);

  // Camera modal state — replaces the old shared-shell video reliance.
  // Two modes so we know what to do with the captured photo.
  const [cameraMode, setCameraMode] = useState(null); // "scan" | "room" | null

  const onAddToRoom = () => setCameraMode("room");
  const onSnap = () => setCameraMode("scan");

  const handleCapture = async (dataUrl) => {
    const mode = cameraMode;
    setCameraMode(null);
    if (!dataUrl || !mode) return;
    if (mode === "room") {
      setRoomBusy(true);
      try {
        const r = await axios.post(`${API}/room/snap`, { space, photo_b64: dataUrl });
        const shot = r.data?.snapshot;
        if (shot?.detected_summary) {
          toast.success(`📸 Added to ${spaceLabel}: ${shot.detected_summary}`);
        } else {
          toast.success(`📸 Added to your ${spaceLabel}.`);
        }
        refreshRoom();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Add-to-room failed.");
      } finally {
        setRoomBusy(false);
      }
      return;
    }
    // mode === "scan"
    setScanning(true);
    try {
      const r = await axios.post(`${API}/equipment/scan`, { photo_b64: dataUrl, space });
      const d = r.data || {};
      setPending({
        photo_b64: dataUrl,
        name: d.name || "",
        category: d.category || "misc",
        tags: d.tags || [],
        ai_confidence: d.confidence || 0,
        source: "ai",
      });
      if (d.categories?.length) setCats(d.categories);
      setTimeout(() => nameEditRef.current?.focus?.(), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vision scan failed. You can still name it manually.");
      setPending({
        photo_b64: dataUrl,
        name: "",
        category: "misc",
        tags: [],
        ai_confidence: 0,
        source: "manual",
      });
    } finally {
      setScanning(false);
    }
  };

  const onDeleteRoomShot = async (snapshot_id) => {
    try {
      await axios.delete(`${API}/room/snap/${snapshot_id}`);
      refreshRoom();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const onConfirm = async () => {
    if (!pending || !pending.name.trim()) {
      toast.error("Give it a name first.");
      nameEditRef.current?.focus?.();
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/equipment/save`, {
        space,
        name: pending.name,
        category: pending.category,
        tags: pending.tags,
        photo_b64: pending.photo_b64,
        ai_confidence: pending.ai_confidence,
        source: pending.source,
      });
      toast.success(`🧰 ${pending.name} added to your ${spaceLabel} loadout`);
      setPending(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (equipment_id, name) => {
    if (!window.confirm(`Remove ${name} from your loadout?`)) return;
    try {
      await axios.delete(`${API}/equipment/${equipment_id}`);
      toast.success("Removed.");
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const onBuildSession = async () => {
    if (items.length === 0) {
      toast.error(`Scan a few ${spaceLabel} items first.`);
      return;
    }
    setBuilderBusy(true);
    setBuilderResult(null);
    try {
      const r = await axios.post(`${API}/equipment/build-session`, {
        space,
        goal: builderGoal || undefined,
        duration_min: 20,
        difficulty,
      });
      setBuilderResult(r.data);
      if (!r.data?.ok) {
        toast.error(r.data?.message || "Couldn't build a session.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Session build failed.");
    } finally {
      setBuilderBusy(false);
    }
  };

  return (
    <div className="p-3 space-y-3" data-testid={`equipment-scanner-${space}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">📸 {spaceLabel} scanner</div>
          <div className="text-sm text-white/80">Snap gear to catalog, or add photos to build your {spaceLabel}.</div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onAddToRoom}
            disabled={roomBusy}
            className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-sky-500 border-2 border-sky-300 text-black text-[10px] font-black uppercase tracking-widest hover:bg-sky-400 shadow-lg shadow-sky-500/40 disabled:opacity-60"
            data-testid={`room-snap-btn-${space}`}
            title="Add this view to your room gallery"
          >
            {roomBusy ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
            + Room
          </button>
          <button
            type="button"
            onClick={onSnap}
            disabled={busy || scanning}
            className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-500 border-2 border-emerald-300 text-black text-[10px] font-black uppercase tracking-widest hover:bg-emerald-400 shadow-lg shadow-emerald-500/40 disabled:opacity-60"
            data-testid={`equipment-snap-btn-${space}`}
            title="Identify a single piece of gear"
          >
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
            ID Gear
          </button>
        </div>
      </div>

      {/* 🖼️ Room gallery — the "build your space from photos" grid */}
      {roomShots.length > 0 && (
        <div data-testid={`room-gallery-${space}`}>
          <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1.5">
            My {spaceLabel} · {roomShots.length} snap{roomShots.length !== 1 ? "s" : ""}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
            {roomShots.map((s) => (
              <div key={s.snapshot_id} className="relative group rounded overflow-hidden border border-white/10 aspect-square" data-testid={`room-shot-${s.snapshot_id}`}>
                <img
                  src={`data:image/jpeg;base64,${s.photo_thumb_b64}`}
                  alt={s.caption || s.detected_summary || "Room snap"}
                  className="w-full h-full object-cover"
                />
                {(s.caption || s.detected_summary) && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] px-1 py-0.5 line-clamp-2">
                    {s.caption || s.detected_summary}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onDeleteRoomShot(s.snapshot_id)}
                  className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/70 hover:bg-red-500/70 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`room-shot-delete-${s.snapshot_id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={10} className="text-red-200" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending confirmation modal (inline) */}
      {pending && (
        <div className="rounded-lg border border-emerald-400/40 bg-black/60 p-3" data-testid={`equipment-pending-${space}`}>
          <div className="flex gap-3">
            {pending.photo_b64 && (
              <img
                src={pending.photo_b64}
                alt="captured"
                className="w-20 h-20 object-cover rounded border border-white/20"
              />
            )}
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">
                  {pending.source === "ai"
                    ? <>AI SUGGESTION · <Sparkles size={10} className="inline" /> {Math.round((pending.ai_confidence || 0) * 100)}% conf</>
                    : "MANUAL ENTRY"}
                </div>
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="p-1 rounded hover:bg-white/10"
                  data-testid="equipment-pending-cancel"
                  aria-label="Cancel"
                >
                  <X size={14} className="text-white/70" />
                </button>
              </div>
              <input
                ref={nameEditRef}
                type="text"
                value={pending.name}
                onChange={(e) => setPending({ ...pending, name: e.target.value })}
                placeholder="Give it a name (e.g. 25 lb Dumbbell)"
                className="w-full bg-black/60 border border-white/20 rounded px-2 py-1 text-sm text-white placeholder-white/40"
                data-testid="equipment-pending-name"
              />
              <select
                value={pending.category}
                onChange={(e) => setPending({ ...pending, category: e.target.value })}
                className="w-full bg-black/60 border border-white/20 rounded px-2 py-1 text-sm text-white"
                data-testid="equipment-pending-category"
              >
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
              <div className="flex flex-wrap gap-1">
                {pending.tags.map((t, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                    {t}
                  </span>
                ))}
                {pending.tags.length === 0 && <span className="text-[10px] text-white/40 italic">no tags</span>}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400 disabled:opacity-60"
                  data-testid="equipment-pending-save"
                >
                  <Check size={12} /> Save
                </button>
                <button
                  type="button"
                  onClick={onSnap}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-black/60 border border-white/20 text-white text-xs font-bold hover:bg-black/80"
                  data-testid="equipment-pending-retake"
                >
                  <RefreshCcw size={12} /> Retake
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loadout list */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
            My {spaceLabel} loadout · {items.length}
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={() => setShowBuilder((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-100 text-[10px] font-bold hover:bg-amber-500/30"
              data-testid={`equipment-builder-toggle-${space}`}
            >
              {space === "kitchen" ? <ChefHat size={11} /> : <Dumbbell size={11} />}
              {space === "kitchen" ? "Cook with this" : "Work out with this"}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="text-xs text-white/50 italic px-2 py-4 text-center border border-dashed border-white/20 rounded">
            Empty loadout. Snap your first piece of gear above.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {items.map((it) => {
              const cat = cats.find((c) => c.id === it.category);
              return (
                <div
                  key={it.equipment_id}
                  className="relative rounded-lg border border-white/10 bg-black/50 p-2 flex gap-2"
                  data-testid={`equipment-item-${it.equipment_id}`}
                >
                  {it.photo_thumb_b64 ? (
                    <img
                      src={`data:image/jpeg;base64,${it.photo_thumb_b64}`}
                      alt={it.name}
                      className="w-14 h-14 object-cover rounded border border-white/20 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded border border-white/20 flex items-center justify-center text-2xl bg-black/40 flex-shrink-0">
                      {cat?.icon || "❓"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{it.name}</div>
                    <div className="text-[10px] text-white/50">{cat?.label || it.category}</div>
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {(it.tags || []).slice(0, 2).map((t, i) => (
                        <span key={i} className="text-[9px] px-1 py-0.5 rounded bg-white/10 text-white/70 truncate max-w-[80px]">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(it.equipment_id, it.name)}
                    className="absolute top-1 right-1 p-1 rounded bg-black/60 hover:bg-red-500/40 opacity-70 hover:opacity-100"
                    data-testid={`equipment-delete-${it.equipment_id}`}
                    aria-label="Remove"
                  >
                    <Trash2 size={10} className="text-red-300" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Session builder — "cook / workout with what you have" */}
      {showBuilder && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-950/30 p-3 space-y-2" data-testid={`equipment-builder-${space}`}>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
            {space === "kitchen" ? "🍳 Cook with what you have" : "💪 Workout with what you have"}
          </div>
          {/* Depth mode picker — how much information the user wants, NOT
              how "skilled" they are. Peer to peer. */}
          <div className="flex items-center gap-1.5" data-testid={`equipment-difficulty-${space}`}>
            {[
              { id: "just_show",   label: "▶ Just moves" },
              { id: "explain_why", label: "◐ With context" },
              { id: "pro_mode",    label: "◉ Pro details" },
            ].map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDifficulty(d.id)}
                className={`flex-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border transition ${
                  difficulty === d.id
                    ? "bg-amber-500 border-amber-300 text-black"
                    : "bg-black/40 border-white/20 text-white/70 hover:border-white/40"
                }`}
                data-testid={`equipment-difficulty-${d.id}`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={builderGoal}
            onChange={(e) => setBuilderGoal(e.target.value)}
            placeholder={space === "kitchen" ? "e.g. quick high-protein dinner" : "e.g. upper body pump, form-first"}
            className="w-full bg-black/60 border border-white/20 rounded px-2 py-1.5 text-sm text-white placeholder-white/40"
            data-testid="equipment-builder-goal"
          />
          <button
            type="button"
            onClick={onBuildSession}
            disabled={builderBusy}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-black text-xs font-black uppercase tracking-widest hover:bg-amber-400 disabled:opacity-60"
            data-testid="equipment-builder-run"
          >
            {builderBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {builderBusy ? "Building…" : "Build my session"}
          </button>
          {builderResult?.session && (
            <>
              <button
                type="button"
                onClick={() => setPlayerOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-black text-xs font-black uppercase tracking-widest hover:bg-emerald-400 mt-2"
                data-testid="equipment-launch-player"
              >
                <GraduationCap size={14} /> ▶ Play with Avatar Coach
              </button>
              <div className="mt-2 p-2 rounded bg-black/60 border border-white/10 text-xs text-white space-y-1.5">
                <div className="text-sm font-bold text-amber-200">{builderResult.session.title || "Session"}</div>
                {builderResult.session.estimated_minutes && (
                  <div className="text-[10px] text-white/60">{builderResult.session.estimated_minutes} min · {difficulty}</div>
                )}
                {builderResult.session.coach_note && (
                  <div className="text-[11px] italic text-white/80 border-l-2 border-amber-400/60 pl-2">
                    {builderResult.session.coach_note}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* 🎓 Guided Session Player — fullscreen avatar teaching (owner: "I want
          both the kitchen and gym full screen when interaction with the user"). */}
      {playerOpen && builderResult?.session && (
        <GuidedSessionPlayer
          session={builderResult.session}
          difficulty={difficulty}
          space={space}
          onClose={() => setPlayerOpen(false)}
        />
      )}

      {/* 📸 Camera modal — opens ONLY when the user taps a scan / add-to-room
          button. Closes right after the shot. */}
      <CameraCapture
        open={cameraMode !== null}
        title={
          cameraMode === "room"
            ? `Add to your ${spaceLabel}`
            : space === "kitchen" ? "Snap a kitchen item" : "Snap gear"
        }
        hint={
          cameraMode === "room"
            ? "Fill the frame with one corner / view. AI captions it."
            : space === "kitchen"
              ? "Frame ONE appliance or utensil clearly"
              : "Frame ONE piece of gear clearly"
        }
        onCapture={handleCapture}
        onClose={() => setCameraMode(null)}
      />
    </div>
  );
}

export default EquipmentScannerPanel;
